// /opt/isp_erp/frontend/src/pages/admin/finanzas/FacturaForm.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import { facturasApi } from '../../../api/finanzas/facturas.api';
import './FacturaForm.css';

export default function FacturaForm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Estado de la factura
  const [factura, setFactura] = useState({
    customer_id: '',
    tipo: 'FACTURA',
    fecha_vencimiento: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0], // +30 días
    observaciones: '',
    items: [
      {
        descripcion: '',
        cantidad: 1,
        precio_unitario: '',
        descuento: 0
      }
    ]
  });

  // Cargar clientes y servicios al montar
  useEffect(() => {
    cargarClientes();
    cargarServicios();
  }, []);

  const cargarClientes = async () => {
    try {
      const response = await axios.get('/customers?estado=ACTIVO');
      setClientes(response.data);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const cargarServicios = async () => {
    try {
      const response = await axios.get('/services?active_only=true');
      setServicios(response.data);
    } catch (error) {
      console.error('Error cargando servicios:', error);
    }
  };

  // Filtrar clientes
  const clientesFiltrados = clientes.filter(c =>
    c.nombres?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.apellidos?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.identificacion?.includes(searchTerm)
  );

  // Agregar un item a la factura
  const agregarItem = () => {
    setFactura({
      ...factura,
      items: [
        ...factura.items,
        {
          descripcion: '',
          cantidad: 1,
          precio_unitario: '',
          descuento: 0
        }
      ]
    });
  };

  // Eliminar un item
  const eliminarItem = (index) => {
    const nuevosItems = factura.items.filter((_, i) => i !== index);
    setFactura({ ...factura, items: nuevosItems });
  };

  // Actualizar un item
  const actualizarItem = (index, campo, valor) => {
    const nuevosItems = [...factura.items];
    nuevosItems[index][campo] = valor;
    setFactura({ ...factura, items: nuevosItems });
  };

  // Calcular totales
  const calcularSubtotal = () => {
    return factura.items.reduce((sum, item) => {
      const precio = parseFloat(item.precio_unitario) || 0;
      const cantidad = parseInt(item.cantidad) || 0;
      const subtotalItem = precio * cantidad;
      return sum + subtotalItem;
    }, 0);
  };

  const calcularDescuentoTotal = () => {
    return factura.items.reduce((sum, item) => {
      const precio = parseFloat(item.precio_unitario) || 0;
      const cantidad = parseInt(item.cantidad) || 0;
      const descuento = parseFloat(item.descuento) || 0;
      return sum + (precio * cantidad * descuento / 100);
    }, 0);
  };

  const calcularIVA = () => {
    const subtotal = calcularSubtotal();
    const descuento = calcularDescuentoTotal();
    const base = subtotal - descuento;
    return base * 0.15; // 15% IVA
  };

  const calcularTotal = () => {
    return calcularSubtotal() - calcularDescuentoTotal() + calcularIVA();
  };

  // Enviar factura
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!factura.customer_id) {
      alert('Seleccione un cliente');
      return;
    }

    if (factura.items.length === 0) {
      alert('Agregue al menos un item');
      return;
    }

    for (let i = 0; i < factura.items.length; i++) {
      const item = factura.items[i];
      if (!item.descripcion || !item.precio_unitario || item.precio_unitario <= 0) {
        alert(`Item ${i + 1}: Complete todos los campos obligatorios`);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await facturasApi.crear(factura);
      alert('✅ Factura creada exitosamente');
      navigate(`/finanzas/facturas/${response.data.id}`);
    } catch (error) {
      console.error('Error creando factura:', error);
      alert(error.response?.data?.detail || 'Error al crear la factura');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="factura-form-container">
      {/* Header */}
      <div className="form-header">
        <div className="form-header-left">
          <div className="form-header-icon">📄</div>
          <div>
            <h1>Nueva Factura</h1>
            <p>Complete los datos para crear una nueva factura</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/finanzas/facturas')}
          className="form-btn-cancel"
        >
          ← Cancelar
        </button>
      </div>

      <form onSubmit={handleSubmit} className="factura-form">
        {/* Selección de cliente */}
        <div className="form-section">
          <h2>Información del Cliente</h2>
          
          <div className="form-group">
            <label className="required">
              Cliente <span className="required-star">*</span>
            </label>

            {/* Buscador de clientes */}
            <input
              type="text"
              placeholder="Buscar cliente por nombre o identificación..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input search-input"
            />

            <select
              value={factura.customer_id}
              onChange={(e) => setFactura({...factura, customer_id: parseInt(e.target.value)})}
              className="form-select"
              required
            >
              <option value="">Seleccione un cliente</option>
              {clientesFiltrados.map(c => (
                <option key={c.id} value={c.id}>
                  {c.nombres} {c.apellidos} - {c.identificacion}
                </option>
              ))}
            </select>
            <div className="clientes-count">
              {clientesFiltrados.length} clientes activos disponibles
            </div>
          </div>
        </div>

        {/* Datos de la factura */}
        <div className="form-section">
          <h2>Datos de la Factura</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Tipo</label>
              <select
                value={factura.tipo}
                onChange={(e) => setFactura({...factura, tipo: e.target.value})}
                className="form-select"
              >
                <option value="FACTURA">Factura</option>
                <option value="PROFORMA">Proforma</option>
                <option value="NOTA_CREDITO">Nota de Crédito</option>
              </select>
            </div>

            <div className="form-group">
              <label className="required">
                Fecha de Vencimiento <span className="required-star">*</span>
              </label>
              <input
                type="date"
                value={factura.fecha_vencimiento}
                onChange={(e) => setFactura({...factura, fecha_vencimiento: e.target.value})}
                className="form-input"
                required
              />
            </div>
          </div>
        </div>

        {/* Items de la factura */}
        <div className="form-section">
          <div className="section-header">
            <h2>Items de la Factura</h2>
            <button
              type="button"
              onClick={agregarItem}
              className="btn-add-item"
            >
              ➕ Agregar Item
            </button>
          </div>

          {factura.items.map((item, index) => (
            <div key={index} className="item-card">
              <div className="item-header">
                <h3>Item #{index + 1}</h3>
                {factura.items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => eliminarItem(index)}
                    className="btn-remove-item"
                    title="Eliminar item"
                  >
                    ✕ Eliminar
                  </button>
                )}
              </div>

              <div className="item-grid">
                <div className="item-field full-width">
                  <label className="required">Descripción *</label>
                  <input
                    type="text"
                    value={item.descripcion}
                    onChange={(e) => actualizarItem(index, 'descripcion', e.target.value)}
                    className="form-input"
                    placeholder="Ej: Internet 100MB"
                    required
                  />
                </div>

                <div className="item-field">
                  <label>Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={item.cantidad}
                    onChange={(e) => actualizarItem(index, 'cantidad', parseInt(e.target.value) || 1)}
                    className="form-input"
                  />
                </div>

                <div className="item-field">
                  <label className="required">P. Unitario $ *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={item.precio_unitario}
                    onChange={(e) => actualizarItem(index, 'precio_unitario', e.target.value)}
                    className="form-input"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="item-field">
                  <label>Dto. %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={item.descuento}
                    onChange={(e) => actualizarItem(index, 'descuento', parseInt(e.target.value) || 0)}
                    className="form-input"
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Selector de servicio/plan (opcional) */}
              <div className="item-service-selector">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      const [tipo, id] = e.target.value.split('-');
                      // Aquí puedes auto-completar datos si es necesario
                    }
                  }}
                  className="service-select"
                >
                  <option value="">Asociar a servicio/plan (opcional)</option>
                  {servicios.map(s => (
                    <optgroup key={s.id} label={s.name}>
                      {s.planes?.map(p => (
                        <option key={p.id} value={`plan-${p.id}`}>
                          {p.name} - ${p.price}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Subtotal del item */}
              <div className="item-subtotal">
                Subtotal: <span>${((parseFloat(item.precio_unitario) || 0) * (parseInt(item.cantidad) || 0)).toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Observaciones */}
        <div className="form-section">
          <h2>Observaciones</h2>
          <div className="form-group">
            <textarea
              value={factura.observaciones}
              onChange={(e) => setFactura({...factura, observaciones: e.target.value})}
              className="form-textarea"
              rows="3"
              placeholder="Notas adicionales para la factura..."
            />
          </div>
        </div>

        {/* Totales */}
        <div className="totales-section">
          <div className="totales-card">
            <div className="total-row">
              <span className="total-label">Subtotal:</span>
              <span className="total-value">${calcularSubtotal().toFixed(2)}</span>
            </div>
            <div className="total-row descuento">
              <span className="total-label">Descuento:</span>
              <span className="total-value">- ${calcularDescuentoTotal().toFixed(2)}</span>
            </div>
            <div className="total-row iva">
              <span className="total-label">IVA (15%):</span>
              <span className="total-value">${calcularIVA().toFixed(2)}</span>
            </div>
            <div className="total-row grand-total">
              <span className="total-label">TOTAL:</span>
              <span className="total-value">${calcularTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/finanzas/facturas')}
            className="btn-secondary"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary"
          >
            {loading ? (
              <>
                <span className="loading-spinner-small"></span>
                Creando...
              </>
            ) : (
              '✅ Crear Factura'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
